
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ShoppingCart, CheckCircle, Lock } from 'lucide-react';
import StepIcon from '../StepIcon';

interface ShoppingListStepProps {
  canGenerateShoppingList: boolean;
  personalShoppingListCount: number;
  totalShoppingListCount: number;
  isActiveFamily?: boolean;
  familyMealPlanCount?: number;
}

const ShoppingListStep: React.FC<ShoppingListStepProps> = ({ 
  canGenerateShoppingList, 
  personalShoppingListCount,
  totalShoppingListCount,
  isActiveFamily = false,
  familyMealPlanCount = 0
}) => {
  const isComplete = personalShoppingListCount > 0; // Complete based on personal contributions
  const isLocked = !canGenerateShoppingList;
  
  const getStepMessage = () => {
    if (isLocked) {
      return "Complete step 2 first by planning at least one meal to unlock smart shopping lists.";
    }
    
    if (isComplete) {
      if (isActiveFamily) {
        return `Perfect! You've generated ${personalShoppingListCount} shopping list${personalShoppingListCount !== 1 ? 's' : ''} for your family's meals. You can create new ones anytime you plan meals together.`;
      }
      return `Perfect! You've generated ${personalShoppingListCount} shopping list${personalShoppingListCount !== 1 ? 's' : ''}. You can create new ones anytime you plan meals.`;
    }
    
    if (isActiveFamily && familyMealPlanCount > 0) {
      return "Generate smart shopping lists for your family's planned meals! Include all the ingredients needed for everyone's meal plans in one organized list.";
    }
    
    return "Generate a smart shopping list with all the ingredients you need for your planned meals ahead.";
  };
  
  const getStepTitle = () => {
    if (isActiveFamily) {
      return 'Generate Family Shopping Lists';
    }
    return 'Generate Shopping List';
  };
  
  const getButtonText = () => {
    if (isActiveFamily) {
      return 'Generate Family Shopping List';
    }
    return 'Generate Shopping List';
  };
  
  return (
    <Card className={`w-full transition-all duration-300 ${
      isLocked 
        ? 'opacity-60 bg-muted/30 border-muted' 
        : isComplete
          ? 'bg-gradient-to-r from-sage/5 to-golden/10 border-sage/20 shadow-md hover:shadow-lg hover:scale-[1.01]'
          : 'bg-white border-border shadow-sm hover:shadow-lg hover:scale-[1.01] hover:border-sage/30'
    }`}>
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <StepIcon type="shopping-list" isComplete={isComplete} isLocked={isLocked} />
          <div className="flex-1 min-w-0">
            <CardTitle className={`text-base sm:text-lg font-playfair ${
              isLocked ? 'text-muted-foreground' : ''
            }`}>
              <span className="leading-tight">{getStepTitle()}</span>
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-0 px-4 sm:px-6">
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
          {getStepMessage()}
        </p>
        {canGenerateShoppingList ? (
          <Link to="/shopping-list">
            <Button className="w-full transition-all duration-200 hover:shadow-md hover:scale-[1.01] bg-sage hover:bg-sage/90">
              <ShoppingCart className="h-4 w-4 mr-2" />
              {getButtonText()}
            </Button>
          </Link>
        ) : (
          <Button disabled className="w-full bg-muted text-muted-foreground cursor-not-allowed">
            <Lock className="h-4 w-4 mr-2" />
            Plan a meal first
          </Button>
        )}
        {isComplete && (
          <div className="bg-golden/10 rounded-lg p-3 border border-golden/20">
            <Link 
              to="/shopping-list" 
              className="flex items-center justify-between text-sage font-medium hover:text-sage/80 transition-colors group"
            >
              <span className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">
                  {personalShoppingListCount} shopping list{personalShoppingListCount !== 1 ? 's' : ''} generated!
                </span>
              </span>
              <span className="text-sm group-hover:translate-x-1 transition-transform">
                View Shopping List →
              </span>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ShoppingListStep;
